import React from 'react';
import compose from 'lodash/fp/compose';
import styled from 'styled-components';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  withTranslation,
  withAdminOrgFunctionProposal,
  withCustomRouter,
  withUserPreferences,
  withOrganisationFunction,
  withReligion,
  withSchoolType,
} from '../../../hoc';
import { Input, Form, ButtonLink, Select, Button } from '../../../common/FormItems';
import Swal from 'sweetalert2';


const Wrapper = styled.div``;

const StyledButtonLink = styled(ButtonLink)`
  span {
    text-transform: uppercase;
  }
`;

const StyledSelect = styled(Select)`
  padding-right: 0.5rem;
`;

const ApproveButton = styled(Button)``;

const RejectButton = styled(Button)`
  background-color: #dc3545;
`;

const approvalOptions = [
  { id: 'N', long_description_eng: 'Pending', long_description_deu: 'Ausstehend' },
  { id: 'A', long_description_eng: 'Approved', long_description_deu: 'Genehmigt' },
  {
    id: 'D',
    long_description_eng: 'Disapproved',
    long_description_deu: 'Abgelehnt',
  },
];

const AdminOrgFunctionProposalActionForm = (props) => {
  const {
    routeParams,
    basePath,
    approveOrgFunctionProposal,
    rejectOrgFunctionProposal,
    orgFunctionLocaleOptions,
    religionLocaleOptions,
    schoolTypeOptions,
    tr,
  } = props;

  const { id: proposalId } = routeParams;
  const navigate = useNavigate();
  const location = useLocation();

  const [formData, setFormData] = React.useState({});
  const [approving, setApproving] = React.useState(false);
  const [rejecting, setRejecting] = React.useState(false);
  const [formLoading, setFormLoading] = React.useState(false);

  const fetchData = async () => {
    if (proposalId) {
      try {
        setFormLoading(true);
        const { adminOrgFunctionProposal } = props;
        const proposals = adminOrgFunctionProposal?.proposals || [];
        const found = proposals.find((p) => String(p.id) === String(proposalId));
        if (found) {
          setFormData(found);
        }
      } finally {
        setFormLoading(false);
      }
    }
  };

  React.useEffect(() => {
    fetchData();
    return () => null;
  }, []);

  React.useEffect(() => {
    window.scroll(0, 0);
  }, []);

  const onApprove = async () => {
    if (approving || rejecting) return;
    try {
      setApproving(true);
      await approveOrgFunctionProposal(formData.id);
      Swal.fire(tr('Success'), tr('Proposal approved successfully'), 'success');
      navigate(basePath);
    } catch (e) {
      Swal.fire(tr('Error'), tr('Failed to approve proposal'), 'error');
    } finally {
      setApproving(false);
    }
  };

  const onReject = async () => {
    if (approving || rejecting) return;
    try {
      setRejecting(true);
      await rejectOrgFunctionProposal(formData.id);
      Swal.fire(tr('Success'), tr('Proposal rejected successfully'), 'success');
      navigate(basePath);
    } catch (e) {
      Swal.fire(tr('Error'), tr('Failed to reject proposal'), 'error');
    } finally {
      setRejecting(false);
    }
  };

  const renderActionButtons = () => {
    if (!location.pathname.endsWith('/update')) {
      return null;
    }
    if (formData.approval !== 'N') {
      return null;
    }
    return (
      <div>
        <ApproveButton
          onClick={onApprove}
          name={tr('Approve')}
          actionButton
          isLoading={approving}
          disabled={rejecting}
        />
        <RejectButton
          onClick={onReject}
          cancelButton
          name={tr('Reject')}
          isLoading={rejecting}
          disabled={approving}
        />
      </div>
    );
  };

  return (
    <Wrapper className="form-wrapper">
      <div className="flex">
        <StyledButtonLink
          to={basePath}
          className="option-btn"
          icon="angle double left"
          name="All Entries"
        />
      </div>
      <div className="row">
        <div className="col-7 ml-10 mt-30 container">
          <div
            className="col-7 ml-10 mt-30 container"
            style={{
              position: 'relative',
              background: 'var(--admincat-color-grey-1)',
            }}
          >
            <br />
            <Form
              onSubmit={(e) => e.preventDefault()}
              formData={formData}
              isLoading={approving || rejecting}
              loadingData={formLoading}
              title="Organisation Function Proposal"
              renderActionButtons={renderActionButtons}
            >
              <Input
                value={formData?.organisation_detail?.name || ''}
                name="organisation"
                label={tr('Organisation')}
                placeholder={tr('Organisation')}
                disabled
                showRequiredAsterisk
              />

              <StyledSelect
                value={formData?.organisation_function || ''}
                name="organisation_function"
                label={tr('Organisation Function')}
                optionDefaultValue={tr('Choose Organisation Function')}
                localeOptions={orgFunctionLocaleOptions}
                disabled
              />

              {formData?.religion && (
                <Select
                  value={formData.religion}
                  name="religion"
                  label={tr('Religion')}
                  optionDefaultValue={tr('Choose Religion')}
                  localeOptions={religionLocaleOptions}
                  disabled
                />
              )}

              {formData?.school_type && (
                <Select
                  value={formData.school_type}
                  name="school_type"
                  label={tr('School Type')}
                  optionDefaultValue={tr('Choose School Type')}
                  options={schoolTypeOptions}
                  disabled
                />
              )}

              <StyledSelect
                value={formData?.approval || ''}
                name="approval"
                label={tr('Status')}
                optionDefaultValue={tr('Choose Status')}
                localeOptions={approvalOptions}
                disabled
              />
            </Form>
          </div>
        </div>
      </div>
    </Wrapper>
  );
};

export default compose(
   withCustomRouter,
  withTranslation,
  withUserPreferences,
  withOrganisationFunction,
  withReligion,
  withSchoolType,
  withAdminOrgFunctionProposal,
)(AdminOrgFunctionProposalActionForm);
